import { AnalyticsBrowser, InAppPlugin } from '@customerio/cdp-analytics-browser';
import Gist, { type GistMessage } from 'customerio-gist-web';
import config from '../../config/config';
import {
  type CustomerIOConfig,
  type CustomerIOInboxAPI,
  type CustomerIOInboxMessage,
  type CustomerIOUserTraits,
  type InAppEventListener,
  type InAppEventType,
  type InAppMessageEventDetail,
} from './types';

const STORAGE_KEY_MOCK_INBOX = 'cio_local_mock_inbox_v1';

const getDefaultMockInboxMessages = (): Array<
  Omit<CustomerIOInboxMessage, 'markOpened' | 'markUnopened' | 'markDeleted' | 'trackClick'>
> => [
  {
    messageId: 'mock_msg_invoice_1',
    opened: false,
    sentAt: new Date(Date.now() - 15 * 60 * 1000), // il y a 15 min
    type: 'invoice',
    topics: ['invoices', 'billing'],
    properties: {
      title: 'Nouvelle facture disponible',
      body: 'Votre facture mensuelle de domiciliation pour le mois en cours est téléchargeable dans votre espace client.',
      link: '/customer-summary',
      actionText: 'Consulter la facture',
      category: 'Facturation',
    },
  },
  {
    messageId: 'mock_msg_doc_2',
    opened: false,
    sentAt: new Date(Date.now() - 2 * 60 * 60 * 1000), // il y a 2 heures
    type: 'document',
    topics: ['documents', 'letters'],
    properties: {
      title: 'Attestation de domiciliation à jour',
      body: 'Votre attestation officielle a été mise à jour par les équipes Digidom et est prête pour vos démarches administratives.',
      link: '/account/documents',
      actionText: 'Voir mes documents',
      category: 'Documents',
    },
  },
  {
    messageId: 'mock_msg_welcome_3',
    opened: true,
    sentAt: new Date(Date.now() - 48 * 60 * 60 * 1000), // il y a 2 jours
    type: 'announcement',
    topics: ['announcements'],
    properties: {
      title: 'Bienvenue sur votre Bureau Virtuel Digidom !',
      body: 'Accédez à l’ensemble de vos services : courriers numérisés, réexpédition, formalités juridiques et facturation en quelques clics.',
      link: 'https://www.digidom.pro',
      actionText: 'Découvrir nos services',
      category: 'Nouveauté',
    },
  },
];

class CustomerIOService {
  private analytics: AnalyticsBrowser | null = null;
  private isInitialized = false;
  private usingDirectGist = false;
  private currentUserId: string | null = null;
  private listeners: Map<InAppEventType, Set<InAppEventListener>> = new Map();
  private globalActionListeners: Set<InAppEventListener> = new Set();
  private gistSetupPromise: Promise<void> | null = null;
  private analyticsReadyPromise: Promise<unknown> | null = null;

  private mockMessages: CustomerIOInboxMessage[] = [];
  private mockListeners: Set<(messages: CustomerIOInboxMessage[]) => void> = new Set();
  private pendingPageCalls: Array<{
    nameOrPath?: string;
    category?: string;
    properties?: Record<string, unknown>;
  }> = [];
  private pendingIdentifyCalls: Array<{ userId: string; traits?: CustomerIOUserTraits }> = [];
  private pendingTrackCalls: Array<{ eventName: string; properties?: Record<string, unknown> }> =
    [];

  /**
   * Initialise le SDK Customer.io pour le web et les messages In-App.
   * Prend en compte la configuration globale (variables d'environnement)
   * et d'éventuelles options passées en paramètre.
   */
  public init(customConfig?: Partial<CustomerIOConfig>): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.isInitialized) {
      return;
    }

    const siteId = customConfig?.siteId ?? config.customerIoSiteId;
    const apiKey = customConfig?.apiKey ?? config.customerIoApiKey;
    const region = customConfig?.region ?? config.customerIoRegion ?? 'eu';
    const cdnUrl =
      customConfig?.cdnUrl ??
      config.customerIoCdnUrl ??
      (region === 'eu' ? 'https://cdp-eu.customer.io' : undefined);
    const inAppEnabled = customConfig?.inAppEnabled ?? config.customerIoInAppEnabled;
    const colorScheme = customConfig?.colorScheme ?? 'auto';
    const anonymousInApp = customConfig?.anonymousInApp ?? true;

    // Gère la visibilité du composant visuel natif Gist selon l'environnement
    this.ensureNativeInboxVisibility();

    if (!inAppEnabled) {
      return;
    }

    if (!siteId && !apiKey) {
      // Configuration absente : l'application continue normalement sans bloquer
      return;
    }

    try {
      // 1. Initialise le tracker officiel Customer.io Journeys (window._cio)
      // Permet le tracking temps réel (People > Profile activity) et l'accès global à window._cio
      if (siteId && typeof window !== 'undefined' && !(window as any)._cioLoaded) {
        (window as any)._cioLoaded = true;
        const _cio = (window as any)._cio || [];
        (window as any)._cio = _cio;
        const methods = ['load', 'identify', 'sidentify', 'track', 'page', 'reset'];
        const factory = (method: string) => {
          return (...args: any[]) => {
            _cio.push([method, ...args]);
          };
        };
        for (const m of methods) {
          if (!_cio[m]) {
            _cio[m] = factory(m);
          }
        }
        if (typeof document !== 'undefined' && !document.getElementById('cio-tracker')) {
          const host =
            region === 'eu' ? 'https://track-eu.customer.io' : 'https://track.customer.io';
          const scriptSrc =
            region === 'eu'
              ? 'https://assets.customer.io/assets/track-eu.js'
              : 'https://assets.customer.io/assets/track.js';

          const script = document.createElement('script');
          script.type = 'text/javascript';
          script.async = true;
          script.id = 'cio-tracker';
          script.setAttribute('data-site-id', siteId);
          script.setAttribute('data-base-url', host);
          script.setAttribute('data-use-array-params', 'true');
          script.src = scriptSrc;
          const s = document.getElementsByTagName('script')[0];
          if (s && s.parentNode) {
            s.parentNode.insertBefore(script, s);
          } else {
            document.head?.appendChild(script);
          }
        }
      }

      if (apiKey) {
        // Mode recommandé : Customer.io Data Pipelines (CDP) avec le plugin In-App
        const plugins = [];
        if (siteId) {
          plugins.push(
            InAppPlugin({
              siteId,
              anonymousInApp,
              colorScheme,
              events: (event: Event) => {
                this.handleInAppCustomEvent(event as CustomEvent);
              },
            }),
          );
        }

        this.analytics = AnalyticsBrowser.load(
          {
            writeKey: apiKey,
            cdnURL: cdnUrl,
            plugins,
          },
          {
            initialPageview: true,
            globalAnalyticsKey: 'cioAnalytics',
          },
        );

        // Nettoie window.analytics._cdn pour éviter que Segment (window.analytics) n'utilise par erreur le CDN Customer.io
        if (
          typeof window !== 'undefined' &&
          (window as any).analytics &&
          (window as any).analytics._cdn &&
          (window as any).analytics._cdn.includes('customer.io')
        ) {
          delete (window as any).analytics._cdn;
        }

        this.pendingPageCalls.forEach((call) => {
          this.analytics?.page(call.category, call.nameOrPath, call.properties);
        });
        this.pendingPageCalls = [];

        this.pendingIdentifyCalls.forEach((call) => {
          this.analytics?.identify(call.userId, call.traits || {});
        });
        this.pendingIdentifyCalls = [];

        this.pendingTrackCalls.forEach((call) => {
          this.analytics?.track(call.eventName, call.properties);
        });
        this.pendingTrackCalls = [];

        if (typeof window !== 'undefined') {
          this.page();
        }

        this.analyticsReadyPromise = Promise.resolve(this.analytics)
          .then(() => {
            if (typeof (this.analytics as any)?.ready === 'function') {
              return (this.analytics as any).ready();
            }
          })
          .catch((err) => {
            console.warn('[Customer.io] Avertissement initialisation AnalyticsBrowser:', err);
          });

        this.isInitialized = true;
      } else if (siteId) {
        // Mode direct : configuration du moteur Gist (In-App) avec le Site ID Customer.io
        this.gistSetupPromise = Gist.setup({
          siteId,
          env: 'prod',
          useAnonymousSession: anonymousInApp,
          colorScheme,
        }).then(() => {
          this.attachGistDirectListeners();
        });

        this.usingDirectGist = true;
        this.isInitialized = true;
      }

      // Initialise les messages mock pour le mode local
      if (this.isInboxVisible()) {
        this.initMockMessages();
      }

      // Support automatique du débogueur Customer.io via ?cio_debug_session=true
      if (
        typeof window !== 'undefined' &&
        window.location.search.includes('cio_debug_session=true')
      ) {
        if (typeof Gist.setupDebugOverlay === 'function') {
          Gist.setupDebugOverlay();
        }
      }
    } catch (error) {
      // Évite tout crash de l'interface en cas d'erreur de chargement externe
      console.error('[Customer.io] Erreur lors de l’initialisation:', error);
    }
  }

  /**
   * Associe les informations de l'utilisateur connecté à Customer.io.
   * Permet le ciblage personnalisé des messages In-App.
   */
  public identify(userId: string | null | undefined, traits?: CustomerIOUserTraits): void {
    if (!userId) {
      this.reset();
      return;
    }

    this.currentUserId = userId;

    // 1. Transmet à Customer.io Journeys via window._cio (People > Profile activity)
    if (
      typeof window !== 'undefined' &&
      (window as any)._cio &&
      typeof (window as any)._cio.identify === 'function'
    ) {
      try {
        (window as any)._cio.identify({
          id: userId,
          ...traits,
        });
      } catch {
        // Ignore
      }
    }

    // 2. Transmet à Customer.io Data Pipelines (CDP)
    if (this.analytics) {
      this.analytics.identify(userId, traits || {});
    } else {
      this.pendingIdentifyCalls.push({ userId, traits });
    }

    // 3. Configure la session utilisateur dans le moteur In-App Gist
    if (this.usingDirectGist) {
      const applyUser = () => {
        void Gist.setUserToken(userId);
        if (traits) {
          Object.entries(traits).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              void Gist.setCustomAttribute(key, String(value));
            }
          });
        }
      };

      if (this.gistSetupPromise) {
        void this.gistSetupPromise.then(applyUser);
      } else {
        applyUser();
      }
    }
  }

  /**
   * Enregistre un changement de page / route dans l'application SPA.
   * Permet aux règles de ciblage d'URL (Page Rules) de Customer.io de s'exécuter
   * en temps réel lors de la navigation React Router.
   */
  public page(nameOrPath?: string, category?: string, properties?: Record<string, unknown>): void {
    const route = nameOrPath || (typeof window !== 'undefined' ? window.location.pathname : '');

    // 1. Transmet à Customer.io Journeys via window._cio
    if (
      typeof window !== 'undefined' &&
      (window as any)._cio &&
      typeof (window as any)._cio.page === 'function'
    ) {
      try {
        (window as any)._cio.page(route);
      } catch {
        // Ignore
      }
    }

    // 2. Transmet à Customer.io Data Pipelines (CDP)
    if (this.analytics) {
      this.analytics.page(category, route, properties);
    } else {
      this.pendingPageCalls.push({ nameOrPath: route, category, properties });
    }

    // 3. Met à jour la route courante pour les Page Rules In-App
    if (this.usingDirectGist) {
      void Gist.setCurrentRoute(route);
    }
  }

  /**
   * Émet un événement personnalisé vers Customer.io.
   */
  public track(eventName: string, properties?: Record<string, unknown>): void {
    // 1. Transmet à Customer.io Journeys via window._cio
    if (
      typeof window !== 'undefined' &&
      (window as any)._cio &&
      typeof (window as any)._cio.track === 'function'
    ) {
      try {
        (window as any)._cio.track(eventName, properties || {});
      } catch {
        // Ignore
      }
    }

    // 2. Transmet à Customer.io Data Pipelines (CDP)
    if (this.analytics) {
      this.analytics.track(eventName, properties);
    } else {
      this.pendingTrackCalls.push({ eventName, properties });
    }
  }

  /**
   * Réinitialise l'identité de l'utilisateur lors de la déconnexion.
   * Nettoie les tokens In-App pour garantir la confidentialité entre sessions.
   */
  public reset(): void {
    this.currentUserId = null;

    // 1. Réinitialise window._cio
    if (
      typeof window !== 'undefined' &&
      (window as any)._cio &&
      typeof (window as any)._cio.reset === 'function'
    ) {
      try {
        (window as any)._cio.reset();
      } catch {
        // Ignore
      }
    }

    // 2. Réinitialise Customer.io CDP
    if (this.analytics) {
      this.analytics.reset();
    } else if (this.usingDirectGist) {
      void Gist.clearUserToken();
      void Gist.clearCustomAttributes();
    }
  }

  /**
   * Écoute un type d'événement In-App spécifique (ouverture, action, fermeture, erreur).
   */
  public on(eventType: InAppEventType, listener: InAppEventListener): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(listener);

    return () => {
      this.off(eventType, listener);
    };
  }

  /**
   * Désabonne un écouteur d'événement In-App.
   */
  public off(eventType: InAppEventType, listener: InAppEventListener): void {
    const set = this.listeners.get(eventType);
    if (set) {
      set.delete(listener);
    }
  }

  /**
   * Écoute spécifiquement les clics ou interactions sur les boutons d'un message In-App.
   */
  public onMessageAction(listener: InAppEventListener): () => void {
    this.globalActionListeners.add(listener);
    return () => {
      this.globalActionListeners.delete(listener);
    };
  }

  /**
   * Indique si le bouton flottant de notification In-App doit être affiché.
   * Règle stricte demandée :
   * - Strictement MASQUÉ en dev, staging et prod/production (`false`).
   * - VISIBLE UNIQUEMENT en environnement local (`config.nodeENV === 'local'` ou domaine local).
   */
  public isFloatingButtonVisible(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    const env = (config.nodeENV || '').toLowerCase();
    // En dev, staging ou prod/production -> Ne JAMAIS afficher le bouton flottant
    if (env === 'dev' || env === 'staging' || env === 'production' || env === 'prod') {
      return false;
    }

    if (config.customerIoInboxFloatingButtonEnabled !== undefined) {
      return Boolean(config.customerIoInboxFloatingButtonEnabled);
    }

    if (config.customerIoInboxEnabled !== undefined) {
      return Boolean(config.customerIoInboxEnabled);
    }

    const isLocalEnv = env === 'local';
    const isLocalHost =
      window.location.hostname === 'localhost' ||
      window.location.hostname.endsWith('.localhost') ||
      window.location.hostname === '127.0.0.1';

    return isLocalEnv || isLocalHost;
  }

  /**
   * Indique si l'inbox est activée (pour le mode local ou l'API).
   */
  public isInboxVisible(): boolean {
    return this.isFloatingButtonVisible();
  }

  /**
   * Accès à l'API de Notification Inbox Customer.io.
   * Supporte le filtrage optionnel par sujet (`topics`).
   */
  public inbox(...topics: string[]): CustomerIOInboxAPI {
    // 1. Accès via l'API officielle native In-App (@customerio/cdp-analytics-browser)
    if (this.analytics && typeof (this.analytics as any).inbox === 'function') {
      const getNativeInbox = async (): Promise<any | null> => {
        if (this.analyticsReadyPromise) {
          try {
            await this.analyticsReadyPromise;
          } catch {
            // Ignore les erreurs éventuelles
          }
        }
        const ajs = this.analytics as any;
        if (!ajs) {
          return null;
        }
        if (typeof ajs.instance?.inbox === 'function') {
          try {
            return ajs.instance.inbox(...topics);
          } catch {
            return null;
          }
        }
        try {
          if (typeof ajs.inbox === 'function' && ajs.instance?.inbox) {
            return ajs.inbox(...topics);
          }
        } catch {
          return null;
        }
        return null;
      };

      return {
        total: async () => {
          let liveCount = 0;
          try {
            const nativeInbox = await getNativeInbox();
            if (nativeInbox && typeof nativeInbox.total === 'function') {
              liveCount = (await nativeInbox.total()) || 0;
            }
          } catch {
            // liveCount reste 0
          }
          const mockCount = this.isInboxVisible() ? this.getMockInboxFiltered(topics).length : 0;
          return liveCount + mockCount;
        },
        totalUnopened: async () => {
          let liveCount = 0;
          try {
            const nativeInbox = await getNativeInbox();
            if (nativeInbox && typeof nativeInbox.totalUnopened === 'function') {
              liveCount = (await nativeInbox.totalUnopened()) || 0;
            }
          } catch {
            // liveCount reste 0
          }
          const mockCount = this.isInboxVisible()
            ? this.getMockInboxFiltered(topics).filter((m) => !m.opened).length
            : 0;
          return liveCount + mockCount;
        },
        messages: async () => {
          let live: CustomerIOInboxMessage[] = [];
          try {
            const nativeInbox = await getNativeInbox();
            if (nativeInbox && typeof nativeInbox.messages === 'function') {
              const liveRaw = await nativeInbox.messages();
              if (Array.isArray(liveRaw)) {
                live = liveRaw.map((msg: any) => this.normalizeNativeInboxMessage(msg));
              }
            }
          } catch {
            // En cas d'erreur ou d'absence de connexion distante, on conserve les mocks en local
          }
          const mocks = this.isInboxVisible() ? this.getMockInboxFiltered(topics) : [];
          return [...live, ...mocks];
        },
        onUpdates: (callback) => {
          let unsubscribeNative = () => {};
          let currentLive: CustomerIOInboxMessage[] = [];
          void (async () => {
            try {
              const nativeInbox = await getNativeInbox();
              if (nativeInbox && typeof nativeInbox.onUpdates === 'function') {
                unsubscribeNative = nativeInbox.onUpdates((liveMsgs: any) => {
                  currentLive = Array.isArray(liveMsgs)
                    ? liveMsgs.map((msg: any) => this.normalizeNativeInboxMessage(msg))
                    : [];
                  const mocks = this.isInboxVisible() ? this.getMockInboxFiltered(topics) : [];
                  callback([...currentLive, ...mocks]);
                });
              }
            } catch {
              // Ignore
            }
          })();

          const unsubscribeMock = this.subscribeMockMessages((msgs) => {
            const mocks = this.filterMessagesByTopics(msgs, topics);
            callback([...currentLive, ...mocks]);
          });

          return () => {
            unsubscribeNative();
            unsubscribeMock();
          };
        },
      };
    }

    // 2. Accès direct via le moteur Gist (customerio-gist-web)
    if (this.usingDirectGist && typeof Gist.getInboxMessages === 'function') {
      const ensureGistReady = async () => {
        if (this.gistSetupPromise) {
          try {
            await this.gistSetupPromise;
          } catch {
            // Continuer
          }
        }
      };

      return {
        total: async () => {
          let liveCount = 0;
          try {
            await ensureGistReady();
            const raw = await Gist.getInboxMessages();
            if (Array.isArray(raw)) {
              liveCount = this.filterGistMessages(raw, topics).length;
            }
          } catch {
            // liveCount reste 0
          }
          const mockCount = this.isInboxVisible() ? this.getMockInboxFiltered(topics).length : 0;
          return liveCount + mockCount;
        },
        totalUnopened: async () => {
          let liveCount = 0;
          try {
            await ensureGistReady();
            const raw = await Gist.getInboxMessages();
            if (Array.isArray(raw)) {
              liveCount = this.filterGistMessages(raw, topics).filter((m) => !m.opened).length;
            }
          } catch {
            // liveCount reste 0
          }
          const mockCount = this.isInboxVisible()
            ? this.getMockInboxFiltered(topics).filter((m) => !m.opened).length
            : 0;
          return liveCount + mockCount;
        },
        messages: async () => {
          let live: CustomerIOInboxMessage[] = [];
          try {
            await ensureGistReady();
            const raw = await Gist.getInboxMessages();
            if (Array.isArray(raw)) {
              live = this.filterGistMessages(raw, topics).map((m) => this.wrapGistMessage(m));
            }
          } catch {
            // live reste vide
          }
          const mocks = this.isInboxVisible() ? this.getMockInboxFiltered(topics) : [];
          return [...live, ...mocks];
        },
        onUpdates: (callback) => {
          const handler = async () => {
            let live: CustomerIOInboxMessage[] = [];
            try {
              const raw = await Gist.getInboxMessages();
              if (Array.isArray(raw)) {
                live = this.filterGistMessages(raw, topics).map((m) => this.wrapGistMessage(m));
              }
            } catch {
              // live reste vide
            }
            const mocks = this.isInboxVisible() ? this.getMockInboxFiltered(topics) : [];
            callback([...live, ...mocks]);
          };

          const attachListener = () => {
            if (Gist.events && typeof Gist.events.on === 'function') {
              Gist.events.on('messageInboxUpdated', handler);
            }
          };

          if (this.gistSetupPromise) {
            void this.gistSetupPromise.then(() => {
              attachListener();
              void handler();
            });
          } else {
            attachListener();
          }

          const unsubscribeMock = this.subscribeMockMessages((msgs) => {
            void (async () => {
              let live: CustomerIOInboxMessage[] = [];
              try {
                const raw = await Gist.getInboxMessages();
                if (Array.isArray(raw)) {
                  live = this.filterGistMessages(raw, topics).map((m) => this.wrapGistMessage(m));
                }
              } catch {
                // live reste vide
              }
              callback([...live, ...this.filterMessagesByTopics(msgs, topics)]);
            })();
          });

          return () => {
            if (Gist.events && typeof Gist.events.off === 'function') {
              Gist.events.off('messageInboxUpdated', handler);
            }
            unsubscribeMock();
          };
        },
      };
    }

    // 3. Mode fallback / mock pour tests en environnement local
    return {
      total: async () => this.getMockInboxFiltered(topics).length,
      totalUnopened: async () => this.getMockInboxFiltered(topics).filter((m) => !m.opened).length,
      messages: async () => this.getMockInboxFiltered(topics),
      onUpdates: (callback) => {
        return this.subscribeMockMessages((msgs) => {
          callback(this.filterMessagesByTopics(msgs, topics));
        });
      },
    };
  }

  /**
   * Ajoute une notification de test dans la boîte de réception mock locale.
   */
  public addMockInboxMessage(custom?: Partial<CustomerIOInboxMessage>): CustomerIOInboxMessage {
    const newMsg = this.createMockInboxMessage({
      messageId: custom?.messageId || `mock_test_${Date.now()}`,
      opened: custom?.opened ?? false,
      sentAt: custom?.sentAt ?? new Date(),
      type: custom?.type ?? 'announcement',
      topics: custom?.topics ?? ['test', 'announcements'],
      properties: custom?.properties ?? {
        title: 'Nouvelle alerte Digidom',
        body: 'Ceci est une notification de test générée en environnement local.',
        category: 'Alerte test',
      },
    });

    if (this.mockMessages.length === 0) {
      this.initMockMessages();
    }
    this.mockMessages.unshift(newMsg);
    this.saveMockMessages();
    this.notifyMockListeners();
    return newMsg;
  }

  /**
   * Réinitialise les notifications mock locales avec le jeu de données par défaut.
   */
  public resetMockInboxMessages(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY_MOCK_INBOX);
    }
    this.initMockMessages();
    this.notifyMockListeners();
  }

  /**
   * Marque tous les messages actuellement reçus comme lus.
   */
  public async markAllInboxMessagesOpened(...topics: string[]): Promise<void> {
    const list = await this.inbox(...topics).messages();
    await Promise.all(list.filter((m) => !m.opened).map((m) => m.markOpened()));
  }

  /**
   * Retourne l'instance sous-jacente du client AnalyticsBrowser si besoin.
   */
  public getAnalytics(): AnalyticsBrowser | null {
    return this.analytics;
  }

  /**
   * Indique si Customer.io a été initialisé.
   */
  public getIsInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Retourne l'identifiant de l'utilisateur actuellement identifié.
   */
  public getCurrentUserId(): string | null {
    return this.currentUserId;
  }

  /**
   * Méthode réservée aux tests unitaires pour réinitialiser l'état du singleton.
   */
  public resetForTesting(): void {
    this.isInitialized = false;
    this.analytics = null;
    this.usingDirectGist = false;
    this.currentUserId = null;
    this.listeners.clear();
    this.globalActionListeners.clear();
    this.mockMessages = [];
    this.mockListeners.clear();
    this.gistSetupPromise = null;
    this.analyticsReadyPromise = null;
    this.pendingPageCalls = [];
    this.pendingIdentifyCalls = [];
    this.pendingTrackCalls = [];
  }

  /**
   * Méthode réservée aux tests pour simuler un événement In-App.
   */
  public dispatchTestEvent(event: CustomEvent): void {
    this.handleInAppCustomEvent(event);
  }

  private ensureNativeInboxVisibility(): void {
    if (typeof document === 'undefined') {
      return;
    }
    const styleId = 'cio-inbox-visibility-style';
    let styleEl = document.getElementById(styleId);
    if (!this.isFloatingButtonVisible()) {
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = styleId;
        styleEl.textContent = `
          #gist-inbox-button, #gist-inbox-panel {
            display: none !important;
            visibility: hidden !important;
            pointer-events: none !important;
          }
        `;
        document.head.appendChild(styleEl);
      }
    } else if (styleEl) {
      styleEl.remove();
    }
  }

  private initMockMessages(): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY_MOCK_INBOX);
      let parsed: any[] | null = null;
      if (raw) {
        parsed = JSON.parse(raw);
      }
      const source =
        Array.isArray(parsed) && parsed.length > 0 ? parsed : getDefaultMockInboxMessages();
      this.mockMessages = source.map((item) => this.createMockInboxMessage(item));
    } catch {
      this.mockMessages = getDefaultMockInboxMessages().map((item) =>
        this.createMockInboxMessage(item),
      );
    }
  }

  private saveMockMessages(): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      const serialized = this.mockMessages.map((m) => ({
        messageId: m.messageId,
        opened: m.opened,
        sentAt: m.sentAt instanceof Date ? m.sentAt.toISOString() : m.sentAt,
        expiresAt: m.expiresAt instanceof Date ? m.expiresAt.toISOString() : m.expiresAt,
        type: m.type,
        topics: m.topics,
        properties: m.properties,
      }));
      localStorage.setItem(STORAGE_KEY_MOCK_INBOX, JSON.stringify(serialized));
    } catch (e) {
      console.error('[Customer.io] Erreur lors de la sauvegarde des messages mock:', e);
    }
  }

  private notifyMockListeners(): void {
    const copy = [...this.mockMessages];
    this.mockListeners.forEach((listener) => {
      try {
        listener(copy);
      } catch (e) {
        console.error('[Customer.io] Erreur dans un écouteur de mise à jour mock:', e);
      }
    });
  }

  private createMockInboxMessage(item: any): CustomerIOInboxMessage {
    const messageId = String(item.messageId || `mock_${Date.now()}`);
    return {
      messageId,
      opened: Boolean(item.opened),
      sentAt: item.sentAt ? new Date(item.sentAt) : new Date(),
      expiresAt: item.expiresAt ? new Date(item.expiresAt) : undefined,
      type: item.type || 'announcement',
      topics: Array.isArray(item.topics) ? item.topics : [],
      properties: item.properties || {},
      markOpened: async () => {
        const target = this.mockMessages.find((m) => m.messageId === messageId);
        if (target && !target.opened) {
          (target as any).opened = true;
          this.saveMockMessages();
          this.notifyMockListeners();
          this.notifyListeners('in-app-inbox:message-opened', { messageId });
        }
      },
      markUnopened: async () => {
        const target = this.mockMessages.find((m) => m.messageId === messageId);
        if (target && target.opened) {
          (target as any).opened = false;
          this.saveMockMessages();
          this.notifyMockListeners();
        }
      },
      markDeleted: async () => {
        this.mockMessages = this.mockMessages.filter((m) => m.messageId !== messageId);
        this.saveMockMessages();
        this.notifyMockListeners();
        this.notifyListeners('in-app-inbox:message-dismissed', { messageId });
      },
      trackClick: (actionName?: string) => {
        this.track('CustomerIO Inbox Message Clicked', {
          messageId,
          actionName: actionName || item.properties?.actionText,
          link: item.properties?.link,
        });
        this.notifyListeners('in-app-inbox:message-action', {
          messageId,
          actionName: actionName || item.properties?.actionText,
          actionValue: item.properties?.link,
        });
      },
    };
  }

  private filterMessagesByTopics(
    messages: CustomerIOInboxMessage[],
    topics: string[],
  ): CustomerIOInboxMessage[] {
    if (!topics || topics.length === 0) {
      return messages;
    }
    return messages.filter((m) => {
      if (!m.topics || m.topics.length === 0) {
        return true;
      }
      return m.topics.some((t) => topics.includes(t));
    });
  }

  public getMockInboxFiltered(topics: string[] = []): CustomerIOInboxMessage[] {
    if (this.mockMessages.length === 0) {
      this.initMockMessages();
    }
    return this.filterMessagesByTopics(this.mockMessages, topics);
  }

  private subscribeMockMessages(
    callback: (messages: CustomerIOInboxMessage[]) => void,
  ): () => void {
    this.mockListeners.add(callback);
    return () => {
      this.mockListeners.delete(callback);
    };
  }

  private filterGistMessages(messages: any[], topics: string[]): any[] {
    if (!topics || topics.length === 0) {
      return messages;
    }
    return messages.filter((m) => {
      const messageTopics = m.topics || [];
      if (messageTopics.length === 0) {
        return true;
      }
      return messageTopics.some((t: string) => topics.includes(t));
    });
  }

  private wrapGistMessage(raw: any): CustomerIOInboxMessage {
    const queueId = raw.queueId || raw.messageId;
    const properties = {
      ...(raw.properties || {}),
      isRealCustomerIO: true,
    };
    return {
      messageId: queueId,
      opened: Boolean(raw.opened),
      sentAt: raw.sentAt ? new Date(raw.sentAt) : new Date(),
      expiresAt: raw.expiry ? new Date(raw.expiry) : undefined,
      type: raw.type || raw.messageType || '',
      topics: Array.isArray(raw.topics) ? raw.topics : [],
      properties,
      markOpened: async () => {
        await Gist.updateInboxMessageOpenState(queueId, true);
      },
      markUnopened: async () => {
        await Gist.updateInboxMessageOpenState(queueId, false);
      },
      markDeleted: async () => {
        await Gist.removeInboxMessage(queueId);
      },
      trackClick: (actionName?: string) => {
        this.track('CustomerIO Inbox Message Clicked', {
          messageId: queueId,
          actionName,
        });
      },
    };
  }

  private normalizeNativeInboxMessage(msg: any): CustomerIOInboxMessage {
    const properties = {
      ...(msg.properties || {}),
      isRealCustomerIO: true,
    };
    return {
      messageId: msg.messageId || msg.id,
      opened: Boolean(msg.opened),
      sentAt: msg.sentAt ? new Date(msg.sentAt) : new Date(),
      expiresAt: msg.expiresAt ? new Date(msg.expiresAt) : undefined,
      type: msg.type || '',
      topics: Array.isArray(msg.topics) ? msg.topics : [],
      properties,
      markOpened: async () => {
        if (typeof msg.markOpened === 'function') {
          await msg.markOpened();
        }
      },
      markUnopened: async () => {
        if (typeof msg.markUnopened === 'function') {
          await msg.markUnopened();
        }
      },
      markDeleted: async () => {
        if (typeof msg.markDeleted === 'function') {
          await msg.markDeleted();
        }
      },
      trackClick: (actionName?: string) => {
        if (typeof msg.trackClick === 'function') {
          msg.trackClick(actionName);
        } else {
          this.track('CustomerIO Inbox Message Clicked', {
            messageId: msg.messageId || msg.id,
            actionName,
          });
        }
      },
    };
  }

  private handleInAppCustomEvent(event: CustomEvent): void {
    const type = event.type as InAppEventType;
    const detail: InAppMessageEventDetail = (event.detail as InAppMessageEventDetail) || {};

    const listeners = this.listeners.get(type);
    if (listeners) {
      listeners.forEach((listener) => {
        try {
          listener(detail, event);
        } catch (err) {
          console.error('[Customer.io] Erreur dans l’écouteur d’événement:', err);
        }
      });
    }

    if (type === 'in-app:message-action' || type === 'in-app-inbox:message-action') {
      this.globalActionListeners.forEach((listener) => {
        try {
          listener(detail, event);
        } catch (err) {
          console.error('[Customer.io] Erreur dans l’écouteur d’action:', err);
        }
      });
    }
  }

  private attachGistDirectListeners(): void {
    if (!Gist.events || typeof Gist.events.on !== 'function') {
      return;
    }

    Gist.events.on('messageShown', (rawMessage: unknown) => {
      const message = rawMessage as GistMessage;
      const detail: InAppMessageEventDetail = {
        messageId: message?.messageId,
        deliveryId: message?.properties?.gist?.campaignId ?? undefined,
      };
      this.notifyListeners('in-app:message-opened', detail);
    });

    Gist.events.on('messageDismissed', (rawMessage: unknown) => {
      const message = rawMessage as GistMessage;
      const detail: InAppMessageEventDetail = {
        messageId: message?.messageId,
        deliveryId: message?.properties?.gist?.campaignId ?? undefined,
      };
      this.notifyListeners('in-app:message-dismissed', detail);
    });

    Gist.events.on('messageAction', (rawParams: unknown) => {
      const params = rawParams as { message?: GistMessage; action?: string; name?: string };
      const detail: InAppMessageEventDetail = {
        messageId: params?.message?.messageId,
        deliveryId: params?.message?.properties?.gist?.campaignId ?? undefined,
        action: params?.action,
        name: params?.name,
        actionName: params?.name,
        actionValue: params?.action,
      };
      this.notifyListeners('in-app:message-action', detail);
    });

    Gist.events.on('messageError', (rawMessage: unknown) => {
      const message = rawMessage as GistMessage;
      const detail: InAppMessageEventDetail = {
        messageId: message?.messageId,
      };
      this.notifyListeners('in-app:message-error', detail);
    });
  }

  private notifyListeners(type: InAppEventType, detail: InAppMessageEventDetail): void {
    const listeners = this.listeners.get(type);
    if (listeners) {
      const customEvent = new CustomEvent(type, { detail });
      listeners.forEach((listener) => {
        try {
          listener(detail, customEvent);
        } catch (err) {
          console.error('[Customer.io] Erreur d’écouteur:', err);
        }
      });
    }

    if (type === 'in-app:message-action' || type === 'in-app-inbox:message-action') {
      const customEvent = new CustomEvent(type, { detail });
      this.globalActionListeners.forEach((listener) => {
        try {
          listener(detail, customEvent);
        } catch (err) {
          console.error('[Customer.io] Erreur d’écouteur d’action:', err);
        }
      });
    }
  }
}

export const customerIoService = new CustomerIOService();

// Fonctions d'aide (helpers) exportées pour une utilisation concise
export const initCustomerIO = (customConfig?: Partial<CustomerIOConfig>): void => {
  customerIoService.init(customConfig);
};

export const cioIdentify = (
  userId: string | null | undefined,
  traits?: CustomerIOUserTraits,
): void => {
  customerIoService.identify(userId, traits);
};

export const cioPage = (
  nameOrPath?: string,
  category?: string,
  properties?: Record<string, unknown>,
): void => {
  customerIoService.page(nameOrPath, category, properties);
};

export const cioTrack = (eventName: string, properties?: Record<string, unknown>): void => {
  customerIoService.track(eventName, properties);
};

export const cioReset = (): void => {
  customerIoService.reset();
};

export const cioOnMessageAction = (listener: InAppEventListener): (() => void) => {
  return customerIoService.onMessageAction(listener);
};

export const cioInbox = (...topics: string[]): CustomerIOInboxAPI => {
  return customerIoService.inbox(...topics);
};

export const cioGetInboxMessages = async (
  ...topics: string[]
): Promise<CustomerIOInboxMessage[]> => {
  return customerIoService.inbox(...topics).messages();
};

export const cioGetInboxUnopenedCount = async (...topics: string[]): Promise<number> => {
  return customerIoService.inbox(...topics).totalUnopened();
};

export const cioOnInboxUpdates = (
  callback: (messages: CustomerIOInboxMessage[]) => void,
  ...topics: string[]
): (() => void) => {
  return customerIoService.inbox(...topics).onUpdates(callback);
};

export const cioIsFloatingButtonVisible = (): boolean => {
  return customerIoService.isFloatingButtonVisible();
};

export const cioIsInboxVisible = (): boolean => {
  return customerIoService.isInboxVisible();
};

export const cioAddMockInboxMessage = (
  custom?: Partial<CustomerIOInboxMessage>,
): CustomerIOInboxMessage => {
  return customerIoService.addMockInboxMessage(custom);
};

export const cioResetMockInboxMessages = (): void => {
  customerIoService.resetMockInboxMessages();
};

export const cioMarkAllInboxMessagesOpened = async (...topics: string[]): Promise<void> => {
  await customerIoService.markAllInboxMessagesOpened(...topics);
};

export default customerIoService;
